// src/features/wf2/popup/W2Editar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getGerencias,
  getCoordenadores,
  getSubs,
  getSbs,
  validarKm,
  sbKm,
  getAtivosNomes,
  getAtividadesNomes,
} from "../../execucao/api";
import api from "../../../api/axios";
import { editarW2 } from "../api";
import "./W2EditarModal.css";
import W2DateErrorModal from "./W2DateErrorModal.jsx";

// >>> popup de justificativa
import W2EditarJustificativa from "./W2EditarJustificativa";

/* ================= Utils ================= */
const norm = (v) => (v == null ? "" : String(v));
const keyify = (s) => String(s || "").trim().toUpperCase();
const foldSearch = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();
const cleanDec = (raw) =>
  String(raw ?? "").replace(",", ".").replace(/[^\d.-]/g, "");

const pickArray = (resp) =>
  Array.isArray(resp)
    ? resp
    : Array.isArray(resp?.data)
    ? resp.data
    : Array.isArray(resp?.data?.data)
    ? resp.data.data
    : [];

const unitFromRow = (row) =>
  row?.unidade ??
  row?.unidade_medida ??
  row?.medida ??
  row?.unid ??
  row?.unidade_de_medida ??
  "";

/* tira .000 / zeros à direita quando vier do banco */
const formatQuantidadeFromDb = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "";
  const num = Number(cleanDec(raw));
  if (!Number.isFinite(num)) return norm(raw);

  if (Number.isInteger(num)) return String(num);

  let s = num.toString();
  if (s.includes("e") || s.includes("E")) {
    s = num.toFixed(6);
  }
  s = s.replace(/(\.\d*?[1-9])0+$/u, "$1").replace(/\.0+$/u, "");
  return s;
};

const firstNonEmpty = (o, keys, fallback = "") => {
  for (const k of keys) {
    const v = o?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
};

/* Normalizador da linha de WF-2 para o front (apenas campos W1->W2) */
const canonProgramacao = (p = {}) => ({
  id: p?.id ?? p?.wf_2_id ?? p?.w2_id ?? null,
  codigo_unico: firstNonEmpty(p, ["codigo_unico", "codigo"]),
  data: firstNonEmpty(p, ["data"]),

  atividade: firstNonEmpty(p, ["atividade", "atividade_nome"]),
  unidade_medida: firstNonEmpty(p, [
    "unidade_de_medida",
    "unidade_medida",
    "unidade",
    "medida",
    "unid",
  ]),
  ativo: firstNonEmpty(p, ["ativo", "ativos", "ativo_nome"]),

  gerencia: firstNonEmpty(p, ["gerencia"]),
  coordenador: firstNonEmpty(p, ["coordenador"]),
  sub: firstNonEmpty(p, ["sub", "subdivisao_id", "sub_id"]),

  kmInicio: firstNonEmpty(p, ["kmInicio", "km_inicio"]),
  kmFinal: firstNonEmpty(p, ["kmFinal", "km_final"]),

  sbOrigem: firstNonEmpty(p, ["sbOrigem", "sb_origem", "patio_origem"]),
  sbDeTrabalho: firstNonEmpty(p, [
    "sbDeTrabalho",
    "sb_de_trabalho",
    "sb_trabalho",
  ]),
  sbEncerramento: firstNonEmpty(p, [
    "sbEncerramento",
    "sb_encerramento",
    "patio_encerramento",
  ]),

  tempo_do_intervalo: firstNonEmpty(p, ["tempo_do_intervalo"]),
  quantidade: p?.quantidade ?? p?.qtd ?? "",
  os: firstNonEmpty(p, ["os"]),
  observacao: firstNonEmpty(p, ["observacao"]),

  nome_do_responsavel: firstNonEmpty(p, ["nome_do_responsavel"]),
  telefone_do_responsavel: firstNonEmpty(p, [
    "telefone_do_responsavel",
    "telefone_do_reponsavel",
    "telefone_responsavel",
  ]),
});

/* Form inicial baseado na linha canonizada */
const buildInitialForm = (prog = {}) => ({
  data: prog.data || "",
  atividade: prog.atividade || "",
  unidade_medida: prog.unidade_medida || "",
  ativo: prog.ativo || "",
  gerencia: prog.gerencia || "",
  coordenador: prog.coordenador || "",
  sub: prog.sub || "",
  kmInicio: prog.kmInicio || "",
  kmFinal: prog.kmFinal || "",
  sbOrigem: prog.sbOrigem || "",
  sbDeTrabalho: prog.sbDeTrabalho || "",
  sbEncerramento: prog.sbEncerramento || "",
  tempo_do_intervalo: prog.tempo_do_intervalo || "",
  quantidade: formatQuantidadeFromDb(prog.quantidade),
  os: prog.os || "",
  observacao: prog.observacao || "",
  kmValido: true,
  sbMsg: "",
});

/* Normalizador de SB e util de unique+sort */
const normalizeSbItem = (x) =>
  x && typeof x === "object"
    ? String(x.sb ?? x.patio ?? x.nome ?? x.label ?? "").trim()
    : String(x ?? "").trim();

const uniqSorted = (arr) =>
  Array.from(new Set(arr.filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, "pt-BR")
  );

const EDIT_TZ = "America/Sao_Paulo";
const EDIT_DATE_WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];

const getTodayYMD = () =>
  new Intl.DateTimeFormat("sv-SE", {
    timeZone: EDIT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

const parseYMDToUtcDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim())) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
};

const formatUtcDateToBR = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", { timeZone: EDIT_TZ }).format(date);
};

const formatUtcDateToYMD = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const startOfUtcMonth = (date) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12, 0, 0));

const addUtcDays = (date, amount) =>
  new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() + amount,
      12,
      0,
      0
    )
  );

const addUtcMonths = (date, amount) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + amount, 1, 12, 0, 0));

const isSameUtcDay = (a, b) =>
  !!a &&
  !!b &&
  a.getUTCFullYear() === b.getUTCFullYear() &&
  a.getUTCMonth() === b.getUTCMonth() &&
  a.getUTCDate() === b.getUTCDate();

function DropdownField({
  label,
  value,
  onChange,
  options = [],
  searchable = false,
  searchPlaceholder = "Digite para filtrar...",
  disabled = false,
  invalid = false,
  fieldRef = null,
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  const safeOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : [])
        .map((opt) =>
          typeof opt === "object"
            ? {
                value: String(opt?.value ?? opt?.label ?? "").trim(),
                label: String(opt?.label ?? opt?.value ?? "").trim(),
              }
            : {
                value: String(opt ?? "").trim(),
                label: String(opt ?? "").trim(),
              }
        )
        .filter((opt) => opt.value),
    [options]
  );

  const selectedOption = useMemo(
    () => safeOptions.find((opt) => opt.value === String(value ?? "")) || null,
    [safeOptions, value]
  );

  const filteredOptions = useMemo(() => {
    const folded = foldSearch(query);
    if (!folded) return safeOptions;
    return safeOptions.filter(
      (opt) =>
        foldSearch(opt.label).includes(folded) ||
        foldSearch(opt.value).includes(folded)
    );
  }, [query, safeOptions]);

  useEffect(() => {
    if (searchable) {
      setQuery(selectedOption?.label || "");
      return;
    }
    setQuery("");
  }, [searchable, selectedOption]);

  useEffect(() => {
    if (!fieldRef) return;
    fieldRef.current = inputRef.current || triggerRef.current;
  });

  useEffect(() => {
    if (!open || !searchable) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open, searchable]);

  useEffect(() => {
    if (!open) {
      setOpenUp(false);
      if (searchable) setQuery(selectedOption?.label || "");
      return undefined;
    }

    const closeOnOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    const updateDirection = () => {
      const rootEl = rootRef.current;
      if (!rootEl) return;
      const rect = rootEl.getBoundingClientRect();
      const boundary =
        rootEl.closest(".editar-modal__body") ||
        rootEl.closest(".editar-modal__container");
      const boundaryRect = boundary?.getBoundingClientRect?.() || null;
      const menuHeight =
        Math.min(Math.max(Math.max(filteredOptions.length, 1) * 40, 56), 220) + 8;
      const spaceBelow = boundaryRect
        ? boundaryRect.bottom - rect.bottom
        : window.innerHeight - rect.bottom;
      const spaceAbove = boundaryRect ? rect.top - boundaryRect.top : rect.top;
      setOpenUp(spaceBelow < menuHeight && spaceAbove > spaceBelow);
    };

    updateDirection();
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnEsc);
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEsc);
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [filteredOptions.length, open, searchable, selectedOption]);

  const triggerClass = `inp editar-select__trigger ${invalid ? "invalid" : ""} ${
    open ? "editar-select__trigger--open" : ""
  } ${searchable && open ? "editar-select__trigger--editing" : ""}`.trim();

  return (
    <div className="editar-field">
      <label className="editar-label">{label}</label>
      <div
        ref={rootRef}
        className={`editar-select ${open ? "editar-select--open" : ""} ${
          openUp ? "editar-select--up" : ""
        } ${disabled ? "editar-select--disabled" : ""}`.trim()}
      >
        {searchable ? (
          open ? (
            <div className={triggerClass} role="combobox" aria-expanded={open} aria-haspopup="listbox">
              <input
                ref={inputRef}
                type="text"
                value={query}
                disabled={disabled}
                className="editar-select__input"
                placeholder={searchPlaceholder}
                onMouseDown={(e) => {
                  if (document.activeElement === inputRef.current) {
                    e.preventDefault();
                    setOpen(false);
                    inputRef.current?.blur();
                  }
                }}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setOpen(false);
                    inputRef.current?.blur();
                  }
                }}
              />
            </div>
          ) : (
            <button
              ref={triggerRef}
              type="button"
              className={triggerClass}
              onClick={() => !disabled && setOpen(true)}
              disabled={disabled}
              aria-haspopup="listbox"
              aria-expanded={open}
            >
              <span className={`editar-select__value ${selectedOption ? "" : "is-placeholder"}`.trim()}>
                {selectedOption?.label || searchPlaceholder}
              </span>
            </button>
          )
        ) : (
          <button
            ref={triggerRef}
            type="button"
            className={triggerClass}
            onClick={() => !disabled && setOpen((prev) => !prev)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className={`editar-select__value ${selectedOption ? "" : "is-placeholder"}`.trim()}>
              {selectedOption?.label || "Selecione..."}
            </span>
          </button>
        )}

        <div
          className={`editar-select__menu ${openUp ? "editar-select__menu--up" : ""}`.trim()}
          role="listbox"
          aria-hidden={!open}
        >
          {filteredOptions.map((opt) => {
            const selected = selectedOption?.value === opt.value;
            return (
              <button
                type="button"
                key={opt.value}
                className={`editar-select__option ${selected ? "is-selected" : ""}`.trim()}
                onClick={() => {
                  setQuery(opt.label);
                  onChange?.({ target: { value: opt.value } });
                  setOpen(false);
                }}
                aria-selected={selected}
              >
                {opt.label}
              </button>
            );
          })}

          {!filteredOptions.length && (
            <div className="editar-select__empty">Nenhuma opção encontrada.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function DateDropdownField({ label, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const rootRef = useRef(null);

  const selectedDate = useMemo(
    () => parseYMDToUtcDate(value) || parseYMDToUtcDate(getTodayYMD()),
    [value]
  );
  const [viewMonth, setViewMonth] = useState(() => startOfUtcMonth(selectedDate));

  useEffect(() => {
    if (!open) {
      setOpenUp(false);
      setViewMonth(startOfUtcMonth(selectedDate));
      return undefined;
    }

    const onOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const updateDirection = () => {
      const rootEl = rootRef.current;
      if (!rootEl) return;
      const rect = rootEl.getBoundingClientRect();
      const boundary =
        rootEl.closest(".editar-modal__body") ||
        rootEl.closest(".editar-modal__container");
      const boundaryRect = boundary?.getBoundingClientRect?.() || null;
      const menuHeight = 330;
      const spaceBelow = boundaryRect
        ? boundaryRect.bottom - rect.bottom
        : window.innerHeight - rect.bottom;
      const spaceAbove = boundaryRect ? rect.top - boundaryRect.top : rect.top;
      setOpenUp(spaceBelow < menuHeight && spaceAbove > spaceBelow);
    };

    updateDirection();
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [open, selectedDate, viewMonth]);

  const monthStart = startOfUtcMonth(viewMonth);
  const gridStart = addUtcDays(monthStart, -monthStart.getUTCDay());
  const calendarDays = Array.from({ length: 42 }, (_, index) => addUtcDays(gridStart, index));
  const todayDate = parseYMDToUtcDate(getTodayYMD());

  return (
    <div className="editar-field">
      <label className="editar-label">{label}</label>
      <div
        ref={rootRef}
        className={`editar-select ${open ? "editar-select--open" : ""} ${
          openUp ? "editar-select--up" : ""
        } editar-date`.trim()}
      >
        <button
          type="button"
          className={`inp editar-select__trigger ${open ? "editar-select__trigger--open" : ""}`.trim()}
          onClick={() => setOpen((prev) => !prev)}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span className="editar-select__value">{formatUtcDateToBR(selectedDate)}</span>
        </button>

        <div
          className={`editar-select__menu editar-date__menu ${openUp ? "editar-select__menu--up" : ""}`.trim()}
          role="dialog"
          aria-hidden={!open}
        >
          <div className="editar-date__header">
            <button
              type="button"
              className="editar-date__nav"
              onClick={() => setViewMonth((prev) => addUtcMonths(prev, -1))}
              aria-label="Mês anterior"
            >
              {"<"}
            </button>
            <div className="editar-date__title">
              {monthStart.toLocaleDateString("pt-BR", {
                month: "long",
                year: "numeric",
                timeZone: "UTC",
              })}
            </div>
            <button
              type="button"
              className="editar-date__nav"
              onClick={() => setViewMonth((prev) => addUtcMonths(prev, 1))}
              aria-label="Próximo mês"
            >
              {">"}
            </button>
          </div>

          <div className="editar-date__weekdays">
            {EDIT_DATE_WEEKDAYS.map((day) => (
              <div key={`${monthStart.toISOString()}-${day}`} className="editar-date__weekday">
                {day}
              </div>
            ))}
          </div>

          <div className="editar-date__grid">
            {calendarDays.map((day) => {
              const isSelected = isSameUtcDay(day, selectedDate);
              const isToday = isSameUtcDay(day, todayDate);
              const isOutside = day.getUTCMonth() !== monthStart.getUTCMonth();
              return (
                <button
                  type="button"
                  key={day.toISOString()}
                  className={`editar-date__day ${isSelected ? "is-selected" : ""} ${
                    isToday ? "is-today" : ""
                  } ${isOutside ? "is-outside" : ""}`.trim()}
                  onClick={() => {
                    onChange?.({ target: { value: formatUtcDateToYMD(day) } });
                    setOpen(false);
                  }}
                >
                  {day.getUTCDate()}
                </button>
              );
            })}
          </div>

          <div className="editar-date__footer">
            <button
              type="button"
              className="editar-date__action editar-date__action--ghost"
              onClick={() => setOpen(false)}
            >
              Fechar
            </button>
            <button
              type="button"
              className="editar-date__action"
              onClick={() => {
                const today = parseYMDToUtcDate(getTodayYMD());
                if (!today) return;
                onChange?.({ target: { value: formatUtcDateToYMD(today) } });
                setViewMonth(startOfUtcMonth(today));
                setOpen(false);
              }}
            >
              Hoje
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= Componente ================= */
export default function W2Editar({
  open = false,
  data = {},
  onClose = () => {},
  onSave,
}) {
  const prog = useMemo(() => canonProgramacao(data), [data]);

  const [gerencias, setGerencias] = useState([]);
  const [coordenadores, setCoordenadores] = useState([]);
  const [subs, setSubs] = useState([]);
  const [sbsAll, setSbsAll] = useState([]);
  const [, setSbsCoord] = useState([]); // setter usado; evita warning de unused
  const [atividades, setAtividades] = useState([]);
  const [ativos, setAtivos] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [dateError, setDateError] = useState("");

  const [form, setForm] = useState(() => buildInitialForm(prog));
  useEffect(() => {
    setForm(buildInitialForm(prog));
  }, [prog]);

  const atividadeRef = useRef(null);
  const ativoRef = useRef(null);
  const qtdRef = useRef(null);

  const [err, setErr] = useState({ open: false, msg: "" });
  const showErr = (msg, focusRef) => {
    setErr({ open: true, msg });
    if (focusRef) setTimeout(() => focusRef.current?.focus?.(), 0);
  };

  // popup justificativa
  const [justOpen, setJustOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  /* Índices de lookup */
  const atividadeIndex = useMemo(() => {
    const map = new Map();
    (atividades || []).forEach((row) => {
      const nome = String(
        row?.atividade || row?.nome || row?.label || row || ""
      ).trim();
      if (!nome) return;
      const k = keyify(nome);
      if (!map.has(k)) map.set(k, { atividade: nome, unidade: unitFromRow(row) });
    });
    return map;
  }, [atividades]);

  const ativoIndex = useMemo(() => {
    const map = new Map();
    (ativos || []).forEach((row) => {
      const nome = String(
        row?.ativos || row?.ativo || row?.nome || row?.label || row || ""
      ).trim();
      if (!nome) return;
      const k = keyify(nome);
      if (!map.has(k)) map.set(k, { ativos: nome });
    });
    return map;
  }, [ativos]);

  const atividadeValida =
    !!(form.atividade && atividadeIndex.has(keyify(form.atividade)));
  const ativoValido = !!(form.ativo && ativoIndex.has(keyify(form.ativo)));

  const atividadesOptions = useMemo(() => {
    const set = new Set(
      (atividades || [])
        .map((r) => String(r?.atividade || r || ""))
        .filter(Boolean)
    );
    if (form.atividade) set.add(form.atividade);
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [atividades, form.atividade]);

  const ativosOptions = useMemo(() => {
    const set = new Set(
      (ativos || [])
        .map((r) => String(r?.ativos || r?.ativo || r || ""))
        .filter(Boolean)
    );
    if (form.ativo) set.add(form.ativo);
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [ativos, form.ativo]);

  const subsIds = useMemo(
    () => subs.map((s) => s.subdivisao_id ?? s.sub ?? s.id).filter(Boolean),
    [subs]
  );

  /* lista de campos alterados (para o popup de justificativa) */
  const camposAlterados = useMemo(() => {
    const out = [];

    if ((prog.data || "") !== (form.data || "")) out.push("Data");
    if ((prog.atividade || "") !== (form.atividade || "")) out.push("Atividade");
    if ((prog.unidade_medida || "") !== (form.unidade_medida || ""))
      out.push("Unidade de Medida");
    if ((prog.ativo || "") !== (form.ativo || "")) out.push("Ativo");
    if ((prog.gerencia || "") !== (form.gerencia || "")) out.push("Gerência");
    if ((prog.coordenador || "") !== (form.coordenador || ""))
      out.push("Coordenador");
    if (String(prog.quantidade ?? "") !== String(form.quantidade ?? ""))
      out.push("Quantidade");
    if ((prog.os || "") !== (form.os || "")) out.push("OS");
    if ((prog.sub || "") !== (form.sub || "")) out.push("SUB");
    if (String(prog.kmInicio ?? "") !== String(form.kmInicio ?? ""))
      out.push("KM Início");
    if (String(prog.kmFinal ?? "") !== String(form.kmFinal ?? ""))
      out.push("KM Final");
    if ((prog.sbOrigem || "") !== (form.sbOrigem || ""))
      out.push("Pátio de Origem");
    if ((prog.sbDeTrabalho || "") !== (form.sbDeTrabalho || ""))
      out.push("SB de Trabalho");
    if ((prog.sbEncerramento || "") !== (form.sbEncerramento || ""))
      out.push("Pátio de Encerramento");
    if ((prog.tempo_do_intervalo || "") !== (form.tempo_do_intervalo || ""))
      out.push("Tempo do Intervalo");
    if ((prog.observacao || "") !== (form.observacao || ""))
      out.push("Observação");

    return out;
  }, [prog, form]);

  /* Loads iniciais */
  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        setGerencias((await getGerencias()) || []);
      } catch {
        setGerencias([]);
      }

      try {
        const r = await api.get("/bases/atividades/listar", {
          params: { limit: 100000 },
        });
        setAtividades(pickArray(r));
      } catch {
        try {
          setAtividades(pickArray(await getAtividadesNomes()));
        } catch {
          setAtividades([]);
        }
      }

      try {
        const r = await api.get("/bases/ativos/listar", {
          params: { limit: 100000 },
        });
        setAtivos(pickArray(r));
      } catch {
        try {
          setAtivos(pickArray(await getAtivosNomes()));
        } catch {
          setAtivos([]);
        }
      }

      const tryUrls = [
        "/bases/sb-origem-encerramento/listar",
        "/sb-origem-encerramento/listar",
      ];
      let all = [];
      for (const url of tryUrls) {
        try {
          const resp = await api.get(url, { params: { limit: 100000 } });
          all = all.concat(pickArray(resp).map(normalizeSbItem));
        } catch {}
      }

      // mantém lista completa; adiciona os valores atuais (se existirem) para aparecer no select
      all = uniqSorted([
        ...all,
        normalizeSbItem(prog.sbOrigem),
        normalizeSbItem(prog.sbEncerramento),
      ]);
      setSbsAll(all);
    })();
  }, [open, prog.sbOrigem, prog.sbEncerramento]);

  /* Gerência -> Coordenadores */
  const prevGer = useRef(form.gerencia);
  useEffect(() => {
    if (!open) return;

    const changed = prevGer.current !== form.gerencia;
    prevGer.current = form.gerencia;

    if (!form.gerencia) {
      setCoordenadores([]);
      return;
    }

    getCoordenadores(form.gerencia)
      .then((l) => setCoordenadores(l || []))
      .catch(() => setCoordenadores([]));

    if (changed) {
      setForm((f) => ({
        ...f,
        coordenador: "",
        sub: "",
        kmValido: true,
        sbMsg: "",
        // >>> IMPORTANTE: não mexe em sbOrigem/sbEncerramento aqui
      }));
      setSubs([]);
      setSbsCoord([]);
    }
  }, [open, form.gerencia]);

  /* Coordenador -> SUBs + SBs */
  const prevCoord = useRef(form.coordenador);
  useEffect(() => {
    if (!open) return;

    const changed = prevCoord.current !== form.coordenador;
    prevCoord.current = form.coordenador;

    if (!form.coordenador) {
      setSubs([]);
      setSbsCoord([]);
      return;
    }

    Promise.all([getSubs(form.coordenador), getSbs(form.coordenador)])
      .then(([subsList, sbsList]) => {
        setSubs(subsList || []);
        setSbsCoord(uniqSorted((sbsList || []).map(normalizeSbItem)));
      })
      .catch(() => {
        setSubs([]);
        setSbsCoord([]);
      });

    if (changed) {
      setForm((f) => ({
        ...f,
        sub: "",
        kmValido: true,
        sbMsg: "",
        // >>> IMPORTANTE: não preenche pátios automaticamente
      }));
    }
  }, [open, form.coordenador]);

  // limpa erro de data ao abrir
  useEffect(() => {
    if (open) setDateError("");
  }, [open]);

  /**
   * FIX DO "BUG":
   * Removido o prefill automático dos pátios com sbsAll[0].
   * Agora:
   * - se vier vazio do banco => fica vazio no select
   * - se vier preenchido => mostra o valor real
   */

  /* SUB + KM -> valida KM e preenche SB de Trabalho */
  const prevTriad = useRef("");
  useEffect(() => {
    if (!open) return;

    const sub = form.sub;
    const ki = Number(cleanDec(form.kmInicio));
    const kf = Number(cleanDec(form.kmFinal));
    const key = [
      sub || "",
      Number.isNaN(ki) ? "" : ki,
      Number.isNaN(kf) ? "" : kf,
    ].join("|");
    if (prevTriad.current === key) return;
    prevTriad.current = key;

    if (sub && Number.isFinite(ki) && Number.isFinite(kf)) {
      Promise.all([validarKm(sub, ki, kf), sbKm(sub, ki, kf)])
        .then(([val, sbsList]) => {
          const arr = (Array.isArray(sbsList) ? sbsList : [])
            .map(normalizeSbItem)
            .filter(Boolean);

          const kmValido = !!val?.existe;
          let sbDeTrabalho = "";
          let sbMsg = "";

          if (kmValido) {
            if (arr.length) {
              sbDeTrabalho = arr.join(", ");
              sbMsg = "";
            } else {
              sbMsg = "Não há SB para o intervalo informado.";
            }
          } else {
            sbMsg = "KM fora da SUB.";
          }

          setForm((f) => ({
            ...f,
            kmValido,
            sbDeTrabalho,
            sbMsg,
          }));
        })
        .catch(() =>
          setForm((f) => ({
            ...f,
            kmValido: false,
            sbDeTrabalho: "",
            sbMsg: "Erro ao buscar KM.",
          }))
        );
    } else {
      setForm((f) => ({
        ...f,
        kmValido: true,
        sbDeTrabalho: "",
        sbMsg: "",
      }));
    }
  }, [open, form.sub, form.kmInicio, form.kmFinal]);

  /* Handlers */
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const handleKmChange = (k, raw) => setField(k, cleanDec(raw));

  const handleAtividadeChange = (e) => {
    const value = e.target.value;
    const uni = atividadeIndex.get(keyify(value))?.unidade || "";
    setForm((f) => ({
      ...f,
      atividade: value,
      unidade_medida: uni,
      ativo: "",
      quantidade: "",
    }));
  };

  // envia para o backend depois da justificativa
  const commitSalvar = async (payload, just) => {
    setSalvando(true);
    try {
      const payloadWithJust = {
        ...payload,
        justificativas_wf2: just?.justificativa ?? null,
        causador: just?.causador ?? null,
        status_wf2: "EDITADO",
      };

      const result = await editarW2(payloadWithJust);
      const r = result?.data ?? result;

      if (typeof onSave === "function") {
        onSave({ ...payloadWithJust, _raw: r, _justificativa: just });
      }
      onClose?.();
    } catch (e) {
      const dataErr = e?.response?.data;
      const msg =
        dataErr?.message ||
        dataErr?.error ||
        e.message ||
        "Erro ao salvar WF-2.";
      showErr(msg);
    } finally {
      setSalvando(false);
      setJustOpen(false);
      setPendingPayload(null);
    }
  };

  /* Salvar (monta payload e abre popup de justificativa) */
  const salvar = () => {
    if (!atividadeValida) {
      return showErr("Selecione uma Atividade válida da lista.", atividadeRef);
    }

    const atividadeOriginalKey = keyify(prog.atividade);
    const atividadeAlterada =
      keyify(form.atividade) !== atividadeOriginalKey && !!form.atividade;

    if (atividadeAlterada) {
      if (!ativoValido) {
        return showErr(
          "Ao alterar a atividade, selecione um Ativo válido.",
          ativoRef
        );
      }
      const qtdNum = Number(cleanDec(form.quantidade));
      if (!(Number.isFinite(qtdNum) && qtdNum > 0)) {
        return showErr(
          "Ao alterar a atividade, informe uma Quantidade maior que zero.",
          qtdRef
        );
      }
    }

    // bloqueia edição para data passada
    const dataKey = String(form.data || prog.data || "")
      .trim()
      .slice(0, 10);
    const todaySp = new Intl.DateTimeFormat("sv-SE", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
    if (dataKey && dataKey < todaySp) {
      setDateError("Data não pode ser no passado. Escolha uma data futura.");
      return;
    }

    const kmInicioNum =
      form.kmInicio !== "" ? Number(cleanDec(form.kmInicio)) : null;
    const kmFinalNum =
      form.kmFinal !== "" ? Number(cleanDec(form.kmFinal)) : null;

    const atividadeCanonica = atividadeIndex.get(keyify(form.atividade));
    const ativoCanonico = ativoIndex.get(keyify(form.ativo));

    const payloadBase = {
      data: form.data || prog.data || null,

      atividade:
        atividadeCanonica?.atividade || form.atividade || prog.atividade || "",
      unidade_de_medida:
        atividadeCanonica?.unidade ||
        form.unidade_medida ||
        prog.unidade_medida ||
        "",
      unidade_medida:
        atividadeCanonica?.unidade ||
        form.unidade_medida ||
        prog.unidade_medida ||
        "",
      ativo: ativoCanonico?.ativos || form.ativo || prog.ativo || "",

      gerencia: form.gerencia || "",
      coordenador: form.coordenador || "",
      sub: form.sub || "",

      kmInicio: kmInicioNum,
      kmFinal: kmFinalNum,
      km_inicio: kmInicioNum,
      km_final: kmFinalNum,

      // pátios: sem fallback; se estiver vazio, envia vazio
      sbOrigem: form.sbOrigem || "",
      sb_origem: form.sbOrigem || "",
      sbEncerramento: form.sbEncerramento || "",
      sb_encerramento: form.sbEncerramento || "",
      sbDeTrabalho: form.sbDeTrabalho || "",
      sb_de_trabalho: form.sbDeTrabalho || "",

      tempo_do_intervalo: form.tempo_do_intervalo || "",

      quantidade: form.quantidade === "" ? null : Number(cleanDec(form.quantidade)),
      qtd: form.quantidade === "" ? null : Number(cleanDec(form.quantidade)),

      os: form.os || "",
      observacao: form.observacao || "",
    };

    const payload = {
      ...payloadBase,
      id: prog.id,
      codigo_unico: prog.codigo_unico,
    };

    setPendingPayload(payload);
    setJustOpen(true);
  };

  const handleJustCancel = () => {
    setJustOpen(false);
    setPendingPayload(null);
  };

  const handleJustConfirm = (just) => {
    if (!pendingPayload) {
      setJustOpen(false);
      return;
    }
    void commitSalvar(pendingPayload, just);
  };

  if (!open) return null;

  return (
    <div className="editar-modal__backdrop" onClick={onClose}>
      <div
        className="editar-modal__container"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="editar-modal__header">
          <h3 className="editar-modal__title">Editar</h3>
          <button
            className="editar-modal__close"
            aria-label="Fechar"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="editar-modal__body">
          <div className="editar-modal__grid grid3">
              {/* linha 0: DATA / ATIVIDADE / ATIVO */}
              <DateDropdownField
                label="DATA"
                value={form.data || ""}
                onChange={(e) => setField("data", e.target.value)}
              />

              <DropdownField
                label="ATIVIDADE"
                value={form.atividade || ""}
                onChange={handleAtividadeChange}
                options={atividadesOptions}
                searchable
                searchPlaceholder="Buscar atividade..."
                invalid={!!(form.atividade && !atividadeValida)}
                fieldRef={atividadeRef}
              />

              <DropdownField
                label="ATIVO"
                value={form.ativo || ""}
                onChange={(e) => setField("ativo", e.target.value)}
                options={ativosOptions}
                searchable
                searchPlaceholder="Buscar ativo..."
                invalid={!!(form.atividade && form.atividade !== "" && form.ativo && !ativoValido)}
                fieldRef={ativoRef}
              />

              {/* linha 1: GERÊNCIA / COORDENADOR / QUANTIDADE */}
              <DropdownField
                label="GERÊNCIA"
                value={form.gerencia || ""}
                onChange={(e) => setField("gerencia", e.target.value)}
                options={gerencias}
              />

              <DropdownField
                label="COORDENADOR"
                value={form.coordenador || ""}
                onChange={(e) => setField("coordenador", e.target.value)}
                options={coordenadores}
                disabled={!form.gerencia}
              />

              <div className="editar-field">
                <label className="editar-label">QUANTIDADE</label>
                <input
                  ref={qtdRef}
                  className="inp"
                  type="text"
                  inputMode="decimal"
                  value={form.quantidade}
                  onChange={(e) => setField("quantidade", cleanDec(e.target.value))}
                />
              </div>

              {/* linha 2: OS / UNIDADE DE MEDIDA / TEMPO DO INTERVALO */}
              <div className="editar-field">
                <label className="editar-label">OS</label>
                <input
                  className="inp"
                  value={form.os || ""}
                  onChange={(e) => setField("os", e.target.value)}
                />
              </div>

              <div className="editar-field">
                <label className="editar-label">UNIDADE DE MEDIDA</label>
                <input className="inp readonly" readOnly value={form.unidade_medida || ""} />
              </div>

              <div className="editar-field">
                <label className="editar-label">TEMPO DO INTERVALO</label>
                <input
                  className="inp"
                  type="time"
                  step={60}
                  value={form.tempo_do_intervalo || ""}
                  onChange={(e) => setField("tempo_do_intervalo", e.target.value)}
                />
              </div>

              {/* linha 3: SUB / KM DE INÍCIO / KM FINAL */}
              <DropdownField
                label="SUB"
                value={form.sub || ""}
                onChange={(e) => setField("sub", e.target.value)}
                options={subsIds}
                disabled={!subsIds.length}
              />

              <div className="editar-field">
                <label className="editar-label">KM DE INÍCIO</label>
                <input
                  className="inp"
                  type="text"
                  inputMode="decimal"
                  value={form.kmInicio}
                  onChange={(e) => handleKmChange("kmInicio", e.target.value)}
                />
              </div>

              <div className="editar-field">
                <label className="editar-label">KM FINAL</label>
                <input
                  className="inp"
                  type="text"
                  inputMode="decimal"
                  value={form.kmFinal}
                  onChange={(e) => handleKmChange("kmFinal", e.target.value)}
                />
              </div>

              {/* linha 4: PÁTIO DE ORIGEM / SB DE TRABALHO / PÁTIO DE ENCERRAMENTO */}
              <DropdownField
                label="PÁTIO DE ORIGEM"
                value={form.sbOrigem || ""}
                onChange={(e) => setField("sbOrigem", e.target.value)}
                options={sbsAll}
                searchable
                searchPlaceholder="Buscar pátio..."
              />

              <div className="editar-field">
                <label className="editar-label">SB DE TRABALHO</label>
                <input
                  className="inp readonly"
                  readOnly
                  value={form.sbDeTrabalho || ""}
                  placeholder="Preenchido por SUB + KM"
                />
                {form.sbMsg && (
                  <small className="editar-label" style={{ textTransform: "none" }}>
                    {form.sbMsg}
                  </small>
                )}
              </div>

              <DropdownField
                label="PÁTIO DE ENCERRAMENTO"
                value={form.sbEncerramento || ""}
                onChange={(e) => setField("sbEncerramento", e.target.value)}
                options={sbsAll}
                searchable
                searchPlaceholder="Buscar pátio..."
              />

              {/* linha 5: OBSERVAÇÃO */}
              <div className="editar-field obs-full-row">
                <label className="editar-label">OBSERVAÇÃO</label>
                <textarea
                  className="inp"
                  rows={2}
                  value={form.observacao}
                  onChange={(e) => setField("observacao", e.target.value)}
                />
              </div>
          </div>
        </div>

        <div className="editar-modal__footer">
          <button className="editar-btn editar-btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="editar-btn editar-btn--primary"
            onClick={salvar}
            disabled={salvando || !atividadeValida}
          >
            {salvando ? "Salvando..." : "Salvar alterações"}
          </button>
        </div>

        {err.open && (
          <div className="ed-alert__backdrop" role="dialog" aria-modal="true">
            <div className="ed-alert__card">
              <div className="ed-alert__title">Verifique os campos</div>
              <div className="ed-alert__msg">{err.msg}</div>
              <button
                className="ed-alert__btn"
                onClick={() => setErr({ open: false, msg: "" })}
              >
                OK
              </button>
            </div>
          </div>
        )}

        <W2EditarJustificativa
          open={justOpen}
          onCancel={handleJustCancel}
          onConfirm={handleJustConfirm}
          camposAlterados={camposAlterados}
        />

        <W2DateErrorModal
          open={!!dateError}
          message={dateError || "Data não pode ser no passado"}
          onClose={() => setDateError("")}
        />
      </div>
    </div>
  );
}
